import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { requiresPayment, getPaymentStatusMessage } from '../utils/paymentUtils';
import PaymentRequiredModal from './PaymentRequiredModal';

/**
 * PaymentProtectedRoute - Wraps ProtectedRoute functionality with payment verification
 * This component ensures users have active payment before accessing premium features
 */
const PaymentProtectedRoute = ({ 
  children, 
  allowSignatureTokens = false,
  redirectTo = '/dashboard' // Where to redirect if payment is required
}) => {
  const { user, loading } = useAuth();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    // Only check payment status after user is loaded and we're not loading
    if (!loading && user) {
      if (requiresPayment(user)) {
        setShowPaymentModal(true);
      }
    }
  }, [user, loading]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Handle signature token access (same as ProtectedRoute)
  if (allowSignatureTokens) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasSignatureToken = urlParams.has('token');
    
    // Allow access if user is logged in OR has a signature token
    // Note: Signature tokens bypass payment requirements for viewing/signing
    if (user || hasSignatureToken) {
      // If user is logged in but has signature token, still check payment for document creation features
      if (user && !hasSignatureToken && requiresPayment(user)) {
        return (
          <>
            {children}
            {showPaymentModal && (
              <PaymentRequiredModal 
                isOpen={showPaymentModal}
                onClose={() => {
                  setShowPaymentModal(false);
                  setShouldRedirect(true);
                }}
                message={getPaymentStatusMessage(user).message}
                redirectPath={getPaymentStatusMessage(user).redirectPath}
                isFreeTrialMessage={getPaymentStatusMessage(user).isFreeTrialMessage}
              />
            )}
            {shouldRedirect && <Navigate to={redirectTo} replace />}
          </>
        );
      }
      return children;
    }
  } else {
    // Regular protected route - requires login
    if (!user) {
      return <Navigate to="/" replace />;
    }
  }

  // User is logged in, now check payment status
  if (requiresPayment(user)) {
    return (
      <>
        {/* Show the page briefly before modal appears for better UX */}
        <div style={{ opacity: showPaymentModal ? 0.3 : 1 }}>
          {children}
        </div>
        
        {showPaymentModal && (
          <PaymentRequiredModal 
            isOpen={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setShouldRedirect(true);
            }}
            message={getPaymentStatusMessage(user).message}
            redirectPath={getPaymentStatusMessage(user).redirectPath}
            isFreeTrialMessage={getPaymentStatusMessage(user).isFreeTrialMessage}
          />
        )}
        
        {shouldRedirect && <Navigate to={redirectTo} replace />}
      </>
    );
  }

  // User has active payment - allow access
  return children;
};

export default PaymentProtectedRoute;